// ---------------------------------------------------------------------------
// Read path. Every query here is additionally filtered by RLS server-side --
// the `.eq()` calls are for narrowing, never for authorization (PLAN 4.6).
// ---------------------------------------------------------------------------

import type {
  Household,
  Profile,
  Turf,
  Visit,
} from '@yucanvas/shared';
import { chunk } from '../lib/chunk';
import { fetchAllPages } from '../lib/paginate';
import { supabase } from '../lib/supabase';

export type TurfSummary = Turf & {
  household_count: number;
  visited_count: number;
};

export async function fetchProfile(userId: string): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data as Profile;
}

/**
 * Turf visible to the signed-in user. Canvassers get their assignments;
 * organizers get everything, because RLS lets them and the dashboard needs it.
 */
export async function fetchTurfs(profile: Profile): Promise<Turf[]> {
  if (profile.role === 'organizer' || profile.role === 'admin') {
    const { data, error } = await supabase.from('turfs').select('*').order('name');
    if (error) throw error;
    return data ?? [];
  }

  const { data, error } = await supabase
    .from('turf_assignments')
    .select('turfs(*)')
    .eq('canvasser_id', profile.id);
  if (error) throw error;

  return (data ?? [])
    .flatMap((row) => (row.turfs ? [row.turfs as Turf] : []))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Coverage counts. Two head-only requests per turf; a canvasser has a handful,
 * so this stays cheap. The organizer dashboard (phase 6) needs a single
 * aggregate query instead.
 */
export async function fetchTurfSummary(turf: Turf): Promise<TurfSummary> {
  const [total, visited] = await Promise.all([
    supabase
      .from('households')
      .select('id', { count: 'exact', head: true })
      .eq('turf_id', turf.id)
      .eq('active', true),
    supabase
      .from('household_status')
      .select('id', { count: 'exact', head: true })
      .eq('turf_id', turf.id)
      .eq('ever_visited', true),
  ]);
  if (total.error) throw total.error;
  if (visited.error) throw visited.error;

  return {
    ...turf,
    household_count: total.count ?? 0,
    visited_count: visited.count ?? 0,
  };
}

export async function fetchTurfHouseholds(turfId: string): Promise<Household[]> {
  // Paged: a turf larger than the project's max-rows cap would otherwise come
  // back silently truncated, sending canvassers to part of their turf.
  const rows = await fetchAllPages((from, to) =>
    supabase
      .from('households')
      // Every column except the generated `location` geometry, which the
      // client must never select: it serialises badly through PostgREST and
      // every spatial operation belongs server-side (PLAN 4.2). Listing the
      // columns also keeps the payload small for the phase 4 delta pull.
      .select(
        'id,street_address,unit,city,state,postal_code,lat,lon,source,source_id,turf_id,do_not_contact,do_not_contact_reason,do_not_contact_at,do_not_contact_by,active,created_at,updated_at',
      )
      .eq('turf_id', turfId)
      .eq('active', true) // soft-deleted rows filtered client-side (PLAN 5.6)
      .order('street_address')
      .range(from, to),
  );
  return rows as unknown as Household[];
}

export async function fetchVisitsForHouseholds(
  householdIds: readonly string[],
): Promise<Visit[]> {
  if (householdIds.length === 0) return [];

  // Two levels of batching: chunked ids keep the `in.(...)` filter inside URL
  // length limits, and each chunk is paged because a turf's visit history
  // grows past the max-rows cap over a campaign.
  const batches = await Promise.all(
    chunk(householdIds, 200).map((ids) =>
      fetchAllPages((from, to) =>
        supabase
          .from('visits')
          .select('*')
          .in('household_id', ids)
          .order('visited_at')
          .range(from, to),
      ),
    ),
  );

  return batches.flat() as unknown as Visit[];
}

export interface HouseholdProgress {
  total: number;
  knocked: number;
  /** Unknocked homes outside completed turf -- what is actually left to do. */
  remaining: number;
}

/**
 * Campaign-wide for an organizer, their own turf for a canvasser: the function
 * runs under the caller's RLS rather than restating it (20260926000100).
 */
export async function fetchHouseholdProgress(): Promise<HouseholdProgress> {
  const { data, error } = await supabase.rpc('household_progress');
  if (error) throw error;
  const row = data?.[0];
  return {
    total: Number(row?.out_total ?? 0),
    knocked: Number(row?.out_knocked ?? 0),
    remaining: Number(row?.out_remaining ?? 0),
  };
}
